import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { TAny } from "@/interfaces/globals";
import { cn } from "@/lib/utils";

export interface LatLng {
  lat: number;
  lng: number;
  label: string;
}

interface SearchLocationInputProps {
  setSelectedLocation: (latLng: LatLng) => void;
  required?: boolean;
  defaultValue?: string;
  label?: string;
  showErrorMessage?: boolean;
}

export const GooglePlaces: React.FC<SearchLocationInputProps> = ({
  setSelectedLocation,
  required = false,
  label,
  defaultValue,
  showErrorMessage = false,
}) => {
  const ref = useRef(null);
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const [suggestedPlaces, setSuggestedPlaces] = useState<TAny>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selected, setSelected] = useState(false);
  const [showError, setShowError] = useState(showErrorMessage);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const autoComplete = new (
      window as TAny
    ).google.maps.places.AutocompleteService();

    if (inputValue.length > 0 && !selected) {
      autoComplete.getPlacePredictions(
        {
          input: inputValue,
        },

        (predictions: TAny[], status: string) => {
          if (status === "OK") {
            setSuggestedPlaces(predictions);
          }
        },
      );

      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [inputValue, selected]);

  const handlePlaceSelection = (place: TAny): void => {
    setInputValue(place.description);
    setSelected(true);

    const placeId = place.place_id;

    const geocoder = new (window as TAny).google.maps.Geocoder();

    geocoder.geocode({ placeId }, (results: TAny[], status: string) => {
      if (status === "OK" && results[0]) {
        const selectedPlace = results?.at(0);
        setSelectedLocation({
          label: place.description,
          lat: selectedPlace.geometry.location.lat(),
          lng: selectedPlace.geometry.location.lng(),
        });
      }
    });
    setShowSuggestions(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value?.trim());
    setSelected(false);
    if (e.target.value.length === 0) {
      setSelectedLocation({ lat: 0, lng: 0, label: "" });
    }
  };

  useEffect(() => {
    setInputValue(defaultValue || "");
    if (defaultValue?.length !== 0) {
      setSelected(true);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (
      showErrorMessage &&
      !selected &&
      inputValue?.trim().length === 0 &&
      required
    ) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [
    inputValue,
    selected,
    showErrorMessage,
    required,
    setSelectedLocation,
    showSuggestions,
  ]);

  const handleClickOutside = (): void => {
    //Close the suggestions when clicked outside
    if (showSuggestions && suggestedPlaces.length > 0) {
      setShowSuggestions(false);
      if (inputValue) {
        setInputValue("");
      }
    }
  };

  useOnClickOutside(ref, handleClickOutside, "mousedown");

  return (
    <div className='space-y-2'>
      <Label
        className={cn("font-normal", showError && "text-destructive")}
        required={required}
      >
        {label || "Search Location"}
      </Label>
      <div className='relative' ref={ref}>
        <Input
          value={inputValue}
          onChange={handleInputChange}
          placeholder='Search Your Address'
          className='text-ellipsis'
          icon={{
            icon: Search,
            position: "right",
          }}
        />
        {showError && <p className='mt-1 text-xs text-destructive'>Required</p>}
        {showSuggestions && (
          <div className='absolute left-0 right-0 z-50 bg-white divide-y shadow-md top-full'>
            {suggestedPlaces.map(
              (place: { place_id: string; description: string }) => (
                <div
                  key={place.place_id}
                  onClick={() => handlePlaceSelection(place)}
                  className='px-4 py-2 cursor-pointer hover:bg-gray-100'
                >
                  {place.description}
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};
